/**
 * `completeTurn` — steps 4–8 of one deterministic turn (architecture.md
 * §7.2): time, the watchers, the agents, the return, and the atomic commit.
 *
 * @remarks
 * Split out of `execute-turn.ts` by D235 for one reason: an answer to an
 * actor's question (`conversation/prompts.ts`) is a TURN with no command —
 * no proposal to narrow, nothing to re-validate, no reach to judge — and it
 * owes the world exactly what a command does afterward: the scheduler's
 * tick, the reveal watcher, the foes' strikes, the triggers judging what the
 * turn made true, the deadlines that yield, the checkpoint return, and one
 * atomic commit advancing time. Two copies of that tail would drift the
 * first time one of them learned a step; this module is the one tail, and
 * `executeTurn` is its first caller.
 *
 * What a caller hands in is a {@link CompletedAction} — the handled half of
 * the turn, its lines stamped against the turn's cursor — and the
 * occurrence the turn raises for the triggers (`command_executed` with the
 * action in hand; `dialogue_answered` with the prompt and the option). The
 * state BEFORE the turn rides along for the one step that compares against
 * it: the checkpoint return reads "an ending selected THIS turn" as a
 * history longer than it was.
 *
 * @packageDocumentation
 */

import type { EventDefinitionId, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { bumpResponseUsage } from '../defaults/default-responses.js';
import { evaluateTriggers, type TriggerEvaluationResult } from '../narrative/evaluate-triggers.js';
import { type OccurrenceFact, tallyOccurrence } from '../narrative/occurrences.js';
import { writeRecordLayers } from '../record/record-of-run.js';
import { type CompletedAction } from './action-handler-result.js';
import { type CheckpointRestoration, restoreCheckpoint } from './checkpoint.js';
import { revealExits } from './exit-visibility.js';
import { strikeAsHostileFoes } from './foe-strikes.js';
import { tickScheduler } from './scheduler-tick.js';
import { type TranscriptCursor } from './transcript-cursor.js';

/** The one atomic product of an executed turn (architecture.md §7.2 step 8). */
export interface TurnExecutionResult {
	/** Fired-event metadata from the scheduler tick (real since R9). */
	readonly firedEventIds: readonly EventDefinitionId[];
	/** The complete next state — transcript appended, `turnCount` advanced. */
	readonly nextState: RuntimeState;
	/** Just this turn's entries (also present at the tail of `nextState.session.transcript`). */
	readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
}

/**
 * Finish a handled turn: let time move, let the watchers and the agents see
 * what it made true, return to the checkpoint when an ending asks, and
 * commit the whole atomically.
 *
 * @param bundle - The loaded bundle
 * @param before - The state the turn started from (no ending, or an older one)
 * @param handled - The handled half of the turn: its state, its stamped entries, the slots it spoke
 * @param occurrence - What this turn IS, for the triggers — a command executed, or an answer given
 * @returns The atomic result
 */
export const completeTurn = (bundle: EngineGameBundle, before: RuntimeState, handled: CompletedAction, occurrence: OccurrenceFact): TurnExecutionResult => {
	// Step 4 — puzzle resolution. RULED CLOSED as D68: there is no separate
	// puzzle subsystem, deliberately. Conditions + effects + locks + containers
	// + latching COMPOSE into puzzles — the POC's three-stage lock chain runs
	// entirely through them — and a dedicated abstraction would be a second
	// mechanism with no case that needs it. The step number is kept so the
	// pipeline's documentation still matches architecture.md §7.2.

	// Step 6 — the scheduler tick (R9: real now). Event entries stamp with the
	// turn's cursor AFTER the action's own entries, so a same-turn epilogue
	// line reads in causal order.
	const tick = tickScheduler(bundle, handled.state, handled.cursor);

	// Step 6b — the reveal watcher (D138): every hidden exit of the room the
	// player now stands in whose condition the action or the tick made true
	// is revealed, once, and heard here — before the triggers, so a trigger
	// sees the revealed world.
	const revealed = revealExits(bundle, tick.state, tick.cursor);

	// Step 6c — the agent step (D234): every hostile foe within reach strikes
	// once, after the player's own act and before the triggers judge the turn
	// — the player first, so a killing blow lands before the counter-strike,
	// and a foe the player has just walked away from has nobody to strike.
	const struck = strikeAsHostileFoes(bundle, revealed.state, revealed.cursor);

	// Step 7 — narrative triggers (R11; R12 added occurrences). The watchers
	// see what the whole turn made true — action AND tick — and the turn
	// itself is tallied FIRST, so a trigger gated "on the tenth wait" fires on
	// the tenth wait, not the eleventh.
	const talliedState = tallyOccurrence(struck.state, occurrence);
	const triggered = revealAfterTriggers(bundle, evaluateTriggers(bundle, talliedState, struck.cursor, occurrence));
	// Step 7b — the deadlines that yield (D188): an event declaring
	// `resolvesAt: 'end_of_turn'` was held back at step 6 and fires now, after
	// the triggers have judged the turn — so an act that beat it on its own
	// due turn has beaten it. Empty for every pack that declares none.
	const settled = settleDeadlines(bundle, triggered, occurrence);
	// Step 7c — the checkpoint return (D142): an ending selected THIS turn
	// that declares `restartAt: 'checkpoint'` has spoken its epilogue; now the
	// world goes back to the photograph and the player arrives there, in the
	// same turn, with the ending cleared so the session never ends.
	// D241: and before it, the Record — an ending this turn selected that the
	// pack's `record.writtenBy` lists leaves its layer in the run's view,
	// remembering the world as the turn left it, so a death remembers where
	// the player died. The checkpoint never photographs the view, so the
	// return below keeps what was written.
	const returned = returnToCheckpoint(bundle, before, writeRecordLayers(bundle, before, settled.state), settled.cursor);
	const turnEntries = [...handled.entries, ...tick.entries, ...revealed.entries, ...struck.entries, ...settled.entries, ...returned.entries];

	// Steps 5 + 8 — advance time and commit atomically. D58: the slots this
	// turn spoke advance their pool cursors in the same atomic commit as the
	// transcript they spoke into.
	const nextState: RuntimeState = {
		...returned.state,
		progression: { ...returned.state.progression, turnCount: returned.state.progression.turnCount + 1 },
		session: {
			...returned.state.session,
			responseUsage: bumpResponseUsage(returned.state.session.responseUsage, [...(handled.spokenSlots ?? []), ...struck.spokenSlots]),
			transcript: [...returned.state.session.transcript, ...turnEntries],
		},
	};

	return { firedEventIds: [...tick.firedEventIds, ...settled.firedEventIds], nextState, transcriptEntries: turnEntries };
};

/**
 * Step 7a — the reveal watcher again, when a trigger changed the world (D199).
 *
 * @remarks
 * Step 6b runs the watcher before the triggers so a trigger sees the revealed
 * world; but a trigger's own effects can be what makes a hidden exit's
 * condition true — Drumline's compass read at the water sets the flag the
 * north-east fold is keyed on — and until this sweep that exit appeared a
 * turn late, spoken over whatever the player did next. The late tick already
 * has this shape (`settleDeadlines`: tick, reveal, triggers); this is the
 * same shape for the triggers themselves. Only when something fired: the
 * turn that fired nothing is left exactly as step 7 left it.
 *
 * @param bundle - The loaded bundle
 * @param triggered - Step 7's result
 * @returns Step 7's result with any newly revealed exits' entries appended
 */
const revealAfterTriggers = (bundle: EngineGameBundle, triggered: TriggerEvaluationResult): TriggerEvaluationResult => {
	if (triggered.firedThisSweep.size === 0) {
		return triggered;
	}

	const revealed = revealExits(bundle, triggered.state, triggered.cursor);

	return { ...triggered, cursor: revealed.cursor, entries: [...triggered.entries, ...revealed.entries], state: revealed.state };
};

/** What step 7 and 7b together produced: the triggers' entries, then the late tick's and whatever it woke. */
interface SettledTurn {
	readonly cursor: TranscriptCursor;
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** Events the END-OF-TURN tick fired (step 6's are reported separately). */
	readonly firedEventIds: readonly EventDefinitionId[];
	readonly state: RuntimeState;
}

/**
 * D188: fire the deadlines that yield, now that the triggers have judged the
 * turn — then let the watchers see what those deadlines did.
 *
 * @remarks
 * The second sweep continues the first's ledger, so a repeatable trigger
 * still fires at most once per turn; a retired one cannot fire again anyway.
 * When the late tick fires nothing there is nothing new to watch, and the
 * turn is exactly what step 7 left — the case every pack without a yielding
 * deadline is always in.
 *
 * @param bundle - The loaded bundle
 * @param triggered - Step 7's result
 * @param occurrence - The turn's occurrence, for the watchers that listen by family
 * @returns The turn after the late tick and its sweep, entries in causal order
 */
const settleDeadlines = (bundle: EngineGameBundle, triggered: TriggerEvaluationResult, occurrence: OccurrenceFact): SettledTurn => {
	const late = tickScheduler(bundle, triggered.state, triggered.cursor, 'end_of_turn');

	if (late.firedEventIds.length === 0) {
		return { cursor: late.cursor, entries: triggered.entries, firedEventIds: [], state: late.state };
	}

	const revealed = revealExits(bundle, late.state, late.cursor);
	const woken = evaluateTriggers(bundle, revealed.state, revealed.cursor, occurrence, triggered.firedThisSweep);

	return {
		cursor: woken.cursor,
		entries: [...triggered.entries, ...late.entries, ...revealed.entries, ...woken.entries],
		firedEventIds: late.firedEventIds,
		state: woken.state,
	};
};

/**
 * D142: restore the checkpoint when an ending selected this turn asks for it.
 *
 * @param bundle - The loaded bundle
 * @param before - The state the turn started from (no ending, or an older one)
 * @param after - The state after action, tick, reveal, and triggers
 * @param cursor - Where the arrival lands
 * @returns The restoration, or `after` untouched when no such ending was selected this turn
 */
const returnToCheckpoint = (
	bundle: EngineGameBundle,
	before: RuntimeState,
	after: RuntimeState,
	cursor: TranscriptCursor,
): CheckpointRestoration => {
	// D151: the history grows by appending, so "an ending selected THIS turn"
	// is a longer history than `before` held; only a terminal ending carries
	// `restartAt`, and a terminal ending is always last.
	const { selectedEndingIds } = after.narrative;
	const latestEndingId = selectedEndingIds.at(-1);

	if (latestEndingId === undefined || selectedEndingIds.length === before.narrative.selectedEndingIds.length) {
		return { cursor, entries: [], state: after };
	}

	const ending = bundle.indexes.endingsById.get(latestEndingId);

	return ending?.restartAt === 'checkpoint' ? restoreCheckpoint(bundle, after, ending, cursor) : { cursor, entries: [], state: after };
};
